import React, { useState, useContext } from 'react';
import { useUpdateEffect } from 'react-use';
import { Flex, Box, Card, Container } from '../../design-system';
import { useData } from '../../helpers/data-loader';
import { errorMsg, checkPermissions } from '../../helpers/utils';
import { Table } from '../../components/Table';
import YearToDateDashboard from '../../components/YearToDateDashboard';
import {
  PERMISSIONS,
  DEADLINE_COLUMNS,
  getDeadlineRowId,
} from '../../globalConstants';
import { Session } from '../../contexts/sessionContext';
import DeadlineCounts from './DeadlineCounts';

const Dashboard = () => {
  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);
  const canViewTeamDashboard = checkPermissions(permissions, [
    PERMISSIONS.READ_TEAM_DEADLINES,
  ]);
  const [isTeamQueue, setQueue] = useState(false);
  const [actions, fetchActions, actionsLoading] = useData('deadline', {
    errorMsg: errorMsg('actions'),
  });

  const isLoading = actionsLoading;

  // The initial fetch happens via the Table's onPageChange effect, which fires
  // on mount with the initial page/sort state — a second mount fetch here
  // would race it, and the two responses can interleave tied-deadline rows in
  // different orders.

  // Fetch data when isTeamQueue changes
  useUpdateEffect(() => {
    fetchActions({ params: { isTeamQueue, hideExpired: true } });
  }, [isTeamQueue]);

  const handlePageChange = ({
    pageIndex,
    pageSize,
    sortColumn,
    sortDesc,
  }: {
    pageIndex: number;
    pageSize: number;
    sortColumn?: string;
    sortDesc?: boolean;
  }) => {
    fetchActions({
      params: {
        isTeamQueue,
        sortColumn,
        sortDesc,
        hideExpired: true,
        page: pageIndex + 1,
        page_size: pageSize,
      },
    });
  };

  const toggleView = () => {
    setQueue(!isTeamQueue);
  };

  return (
    <Container fluid gutter={50} pb={16}>
      <Flex mb={0} mt={1}>
        <Box
          m={0}
          p={2}
          ml={0}
          pl={0}
          flex={1}
          style={{ minWidth: '300px', maxWidth: '400px' }}
        >
          <DeadlineCounts />
        </Box>
        <Box m={0} p={0} ml={0} pl={0} flex={2}>
          <YearToDateDashboard height={380} />
        </Box>
      </Flex>
      <hr className="pb-2 my-4" />
      <Flex mb={1} />
      <Card>
        {canViewTeamDashboard && (
          <div>
            <div className="flex space-x-2 px-7 py-5">
              <span>My Queue</span>
              <button
                id={`queue-button-${isTeamQueue}`}
                type="button"
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none ${
                  isTeamQueue ? 'bg-accent-color' : ''
                }`}
                role="switch"
                aria-checked="false"
                onClick={toggleView}
              >
                <span className="sr-only">Use setting</span>
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 translate-x-0 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isTeamQueue ? 'translate-x-5' : ''
                  }`}
                />
              </button>
              <span>Team Queue</span>
            </div>
          </div>
        )}
        <hr className="pb-2" />
        <Table
          onPageChange={handlePageChange}
          columns={DEADLINE_COLUMNS}
          data={actions}
          loading={isLoading}
          getRowId={getDeadlineRowId}
        />
      </Card>
    </Container>
  );
};

export default Dashboard;
